import { SetFailureStateAndRouteToFailure, FailureContinuation } from './actions/FailureActions';
import { TranscriptionRoutePathNames } from './routes';
import { NetworkError, NetworkErrors } from '../utils/Errors';
import { LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE } from './LoadTranscriptionStrings';
import { LogEventNames } from '../backend/interfaces/ITelemetryLogger';
import { TranscriptionTelemetryErrorCodes } from '../utils/TranscriptionTelemetryErrorCodes';
import { ITranscriptionErrorEvent } from '../backend/interfaces/ITranscriptionErrorEvent'

export enum NetworkConnectionMonitorConfig {
  ShowTransientError,   // Shows a temporary error page, that usually silently reloads after the network connection is restored
  ShowTerminalError     // Shows a terminal error page, that usually means that the user has to close and reopen the pane
}

// Monitors user's network connection status and manages UI accordingly.
export class NetworkConnectionMonitor {
    private history: any;
    private wentOffline: boolean = false;
    private config: NetworkConnectionMonitorConfig | null = null;

    private onOnline = () => {
      if (!this.wentOffline)
        return;

      // Refresh add-in to start recovery experience.
      location.reload();
    }

    private onOffline = () => {
      // Only show offline error message in Recording and Loading pages.
      if (this.history.location.pathname != TranscriptionRoutePathNames.Recording && this.history.location.pathname != TranscriptionRoutePathNames.Loading)
        return;

      this.wentOffline = true;

      // Once the user goes offline, we don't care about them going offline again.
      window.removeEventListener('offline', this.onOffline);

      let transcriptionErrorEvent : ITranscriptionErrorEvent = {errorEventMessage : LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE, errorEventName: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE};

      // TranscriptionStrings can be null if the user goes offline before TranscriptionStrings is loaded during boot.
      if (TranscriptionStrings != null) {
        if ( this.history.location.pathname == TranscriptionRoutePathNames.Recording ) {
          transcriptionErrorEvent = {errorEventMessage : TranscriptionStrings.RecordingLostConnectionFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.RecordingLostConnectionFailureReason};
        } else {
          transcriptionErrorEvent = {errorEventMessage : TranscriptionStrings.RecordingLostConnectionNoBackupFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.RecordingLostConnectionNoBackupFailureReason};
        }
      }

      // Navigate to the failure page.
      SetFailureStateAndRouteToFailure(
        LogEventNames.Warning,
        transcriptionErrorEvent,
        FailureContinuation.None /*failureContinuation*/,
        this.history);
    }

    private onOfflineTerminal = () => {
      // Only show offline error message in Loading page for file upload.
      if (this.history.location.pathname != TranscriptionRoutePathNames.Loading)
        return;

      const transcriptionErrorEvent : ITranscriptionErrorEvent = {errorEventMessage : TranscriptionStrings.LostInternetConnectionReason, errorEventName: TranscriptionTelemetryErrorCodes.LostInternetConnectionReason};

      // Navigate to the failure page.
      // We can't show a retry button because AL refuses to reconnect after an internet disconnection
      SetFailureStateAndRouteToFailure(
        LogEventNames.Warning,
        transcriptionErrorEvent,
        FailureContinuation.None /*failureContinuation*/,
        this.history);
    }

    private clearConfig() {
      if (this.config == NetworkConnectionMonitorConfig.ShowTerminalError) {
        window.removeEventListener('offline', this.onOfflineTerminal);
      }

      if (this.config == NetworkConnectionMonitorConfig.ShowTransientError) {
        window.removeEventListener('online', this.onOnline);
        window.removeEventListener('offline', this.onOffline);
      }
    }

    public SetConfig(config: NetworkConnectionMonitorConfig, history: any) {
      this.history = history
      if (this.config == config)
        return;

      this.clearConfig();
      switch (config) {
        case NetworkConnectionMonitorConfig.ShowTransientError:
          window.addEventListener('online', this.onOnline);
          window.addEventListener('offline', this.onOffline);
          break;

        case NetworkConnectionMonitorConfig.ShowTerminalError:
          window.addEventListener('offline', this.onOfflineTerminal);
          break;
      }

      this.config = config;
    }
  }

// Throws error if the user is not online
export function CheckNetworkConnection() {
  if (!IsConnectedToNetwork()) {
    throw new NetworkError(NetworkErrors.NoInternetConnection, "User is not connected to the internet");
  }
}

export function IsConnectedToNetwork() {
  return navigator.onLine;
}

const networkConnectionMonitor: NetworkConnectionMonitor = new NetworkConnectionMonitor();

export function GetNetworkConnectionMonitorInstance(): NetworkConnectionMonitor {
  return networkConnectionMonitor;
}